import { z } from "zod/v3";

import { getApiV1DocumentWorkflowDocumentsQuerySchema } from "../api/generated";
import type { DocumentWorkflowDocumentsQuery } from "../entities/document-workflow";

export const documentCreateSearchSchema = z
  .object({
    signed_version_id: z.string().uuid().optional(),
    signed_file_id: z.string().uuid().optional(),
  })
  .refine(
    (search) =>
      Boolean(search.signed_version_id) === Boolean(search.signed_file_id),
  );

// Жизненный цикл делопроизводства по порядку: документ поступил, его
// зарегистрировали, прочитали, взяли на исполнение и исполнили. Отмена стоит
// в стороне — это отказ от дальнейшей работы, а не очередной шаг.
export const statusOrder = [
  "received",
  "registered",
  "read",
  "in_progress",
  "completed",
  "cancelled",
] as const;

export type DocumentStatus = (typeof statusOrder)[number];

// Фильтровать разрешаем только по статусам из контракта Gateway: значение вне
// этого списка сгенерированный SDK отклонит ещё до отправки запроса. Список
// берём из схемы SDK, чтобы после перегенерации контракта фильтр подхватил
// новые статусы сам.
const contractStatuses = getApiV1DocumentWorkflowDocumentsQuerySchema
  .unwrap()
  .shape.status.unwrap().element.options;
export const priorities = ["low", "normal", "high", "critical"] as const;
export const dueStates = [
  "normal",
  "due_soon_24h",
  "due_soon_2h",
  "overdue",
  "completed",
] as const;
export const types = ["incoming", "outgoing", "internal"] as const;
export const sorts = [
  "-received_at",
  "received_at",
  "due_at",
  "-due_at",
  "subject",
  "-subject",
  "priority",
  "-priority",
  "status",
  "-status",
] as const;

export type Status = (typeof contractStatuses)[number];

export const statuses: readonly Status[] = statusOrder.filter(
  (status): status is Status =>
    (contractStatuses as readonly string[]).includes(status),
);

export type Priority = (typeof priorities)[number];
export type DueState = (typeof dueStates)[number];
export type DocumentType = (typeof types)[number];
export type Sort = (typeof sorts)[number];

export interface DocumentWorkflowSearch {
  page: number;
  per_page: number;
  search: string;
  type: DocumentType | "";
  status: Status[];
  priority: Priority[];
  assignee_id: string;
  controller_id: string;
  counterparty_id: string;
  mailbox_id: string;
  category_id: string;
  folder_id: string;
  unread: "all" | "true" | "false";
  due_state: DueState[];
  received_from: string;
  received_to: string;
  due_from: string;
  due_to: string;
  signature: "all" | "signed" | "unsigned";
  sort: Sort;
  // Карточка документа, открытая поверх реестра. Хранится в адресе, чтобы на
  // неё можно было перейти ссылкой — например, сразу после создания документа.
  document_id: string;
  tab:
    | "overview"
    | "details"
    | "mail"
    | "discussion"
    | "files"
    | "approval"
    | "timeline";
  message: string;
}

export const defaultSearch: DocumentWorkflowSearch = {
  page: 1,
  per_page: 25,
  search: "",
  type: "",
  status: [],
  priority: [],
  assignee_id: "",
  controller_id: "",
  counterparty_id: "",
  mailbox_id: "",
  category_id: "",
  folder_id: "",
  unread: "all",
  due_state: [],
  received_from: "",
  received_to: "",
  due_from: "",
  due_to: "",
  signature: "all",
  sort: "-received_at",
  document_id: "",
  tab: "overview",
  message: "",
};

export const statusLabels: Record<DocumentStatus, string> = {
  received: "Поступил",
  registered: "Зарегистрирован",
  read: "Прочитан",
  in_progress: "На исполнении",
  completed: "Исполнен",
  cancelled: "Отменён",
};

export const priorityLabels: Record<Priority, string> = {
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
  critical: "Критический",
};

export const typeLabels: Record<DocumentType, string> = {
  incoming: "Входящий",
  outgoing: "Исходящий",
  internal: "Внутренний",
};

export const dueLabels: Record<DueState, string> = {
  normal: "В срок",
  due_soon_24h: "Срок скоро",
  due_soon_2h: "Срочно",
  overdue: "Просрочено",
  completed: "Завершено",
};

export const sortLabels: Record<Sort, string> = {
  "-received_at": "Получены: новые",
  received_at: "Получены: старые",
  due_at: "Срок: раньше",
  "-due_at": "Срок: позже",
  subject: "Тема А-Я",
  "-subject": "Тема Я-А",
  priority: "Приоритет: низкий",
  "-priority": "Приоритет: высокий",
  status: "Статус А-Я",
  "-status": "Статус Я-А",
};

const searchFilterKeys = {
  исполнитель: "assignee_id",
  исполнитель_id: "assignee_id",
  assignee: "assignee_id",
  контролёр: "controller_id",
  контролер: "controller_id",
  controller: "controller_id",
  контрагент: "counterparty_id",
  counterparty: "counterparty_id",
  кпо: "mailbox_id",
  mailbox: "mailbox_id",
  категория: "category_id",
  category: "category_id",
  папка: "folder_id",
  folder: "folder_id",
} as const;

type SearchFilterKey = (typeof searchFilterKeys)[keyof typeof searchFilterKeys];

export interface ParsedRegistrySearch {
  text: string;
  filters: Partial<Pick<DocumentWorkflowSearch, SearchFilterKey>>;
}

export function parseRegistrySearch(value: string): ParsedRegistrySearch {
  const filters: Partial<Pick<DocumentWorkflowSearch, SearchFilterKey>> = {};
  const text: string[] = [];

  for (const part of value.split(",")) {
    const trimmed = part.trim();
    const [rawKey, ...rawValue] = trimmed.split(":");
    const key = (rawKey ?? "")
      .trim()
      .toLowerCase() as keyof typeof searchFilterKeys;
    const filterKey = searchFilterKeys[key];
    const filterValue = rawValue.join(":").trim();

    if (filterKey !== undefined && filterValue !== "") {
      filters[filterKey] = filterValue;
    } else if (trimmed !== "") {
      text.push(trimmed);
    }
  }

  return { text: text.join(", "), filters };
}

function one(value: unknown): string {
  if (Array.isArray(value)) return typeof value[0] === "string" ? value[0] : "";
  if (typeof value === "boolean" || typeof value === "number") {
    return String(value);
  }
  return typeof value === "string" ? value : "";
}

function numberSearch(value: unknown, fallback: number, max: number): number {
  const parsed = Number(one(value));
  if (!Number.isInteger(parsed) || parsed < 1) return fallback;
  return Math.min(parsed, max);
}

function enumValue<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T | "",
): T | "" {
  const raw = one(value);
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

function requiredEnumValue<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  const raw = one(value);
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

function enumArray<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T[] {
  const raw = Array.isArray(value) ? value : one(value).split(",");
  return raw.filter(
    (item): item is T =>
      typeof item === "string" && allowed.includes(item as T),
  );
}

export function validateDocumentWorkflowSearch(
  search: Record<string, unknown>,
): DocumentWorkflowSearch {
  return {
    page: numberSearch(search.page, defaultSearch.page, 10_000),
    per_page: numberSearch(search.per_page, defaultSearch.per_page, 100),
    search: one(search.search),
    type: enumValue(search.type, types, ""),
    status: enumArray(search.status, statuses),
    priority: enumArray(search.priority, priorities),
    assignee_id: one(search.assignee_id),
    controller_id: one(search.controller_id),
    counterparty_id: one(search.counterparty_id),
    mailbox_id: one(search.mailbox_id),
    category_id: one(search.category_id),
    folder_id: one(search.folder_id),
    unread: requiredEnumValue(
      search.unread,
      ["all", "true", "false"] as const,
      "all",
    ),
    due_state: enumArray(search.due_state, dueStates),
    received_from: one(search.received_from),
    received_to: one(search.received_to),
    due_from: one(search.due_from),
    due_to: one(search.due_to),
    signature: requiredEnumValue(
      search.signature,
      ["all", "signed", "unsigned"] as const,
      "all",
    ),
    sort: requiredEnumValue(search.sort, sorts, "-received_at"),
    document_id: one(search.document_id ?? search.document),
    tab: requiredEnumValue(
      search.tab,
      [
        "overview",
        "details",
        "mail",
        "discussion",
        "files",
        "approval",
        "timeline",
      ] as const,
      "overview",
    ),
    message: one(search.message ?? search.mail),
  };
}

export function compactSearch(
  search: DocumentWorkflowSearch,
): Partial<DocumentWorkflowSearch> {
  const compact: Partial<DocumentWorkflowSearch> = {};
  for (const [key, value] of Object.entries(search) as [
    keyof DocumentWorkflowSearch,
    DocumentWorkflowSearch[keyof DocumentWorkflowSearch],
  ][]) {
    const fallback = defaultSearch[key];
    if (Array.isArray(value) && value.length === 0) continue;
    if (Array.isArray(value) || value !== fallback) {
      compact[key] = value as never;
    }
  }
  return compact;
}

export function buildDocumentsQuery(
  search: DocumentWorkflowSearch,
): DocumentWorkflowDocumentsQuery {
  const parsedSearch = parseRegistrySearch(search.search);
  const filterValue = (key: SearchFilterKey): string =>
    parsedSearch.filters[key] ?? search[key];

  return {
    page: search.page,
    per_page: search.per_page,
    sort: search.sort,
    ...(parsedSearch.text === "" ? {} : { search: parsedSearch.text }),
    ...(search.type === "" ? {} : { type: search.type }),
    ...(search.status.length === 0 ? {} : { status: search.status }),
    ...(search.priority.length === 0 ? {} : { priority: search.priority }),
    ...(filterValue("assignee_id") === ""
      ? {}
      : { assignee_id: filterValue("assignee_id") }),
    ...(filterValue("controller_id") === ""
      ? {}
      : { controller_id: filterValue("controller_id") }),
    ...(filterValue("counterparty_id") === ""
      ? {}
      : { counterparty_id: filterValue("counterparty_id") }),
    ...(filterValue("mailbox_id") === ""
      ? {}
      : { mailbox_id: filterValue("mailbox_id") }),
    ...(filterValue("category_id") === ""
      ? {}
      : { category_id: filterValue("category_id") }),
    ...(filterValue("folder_id") === ""
      ? {}
      : { folder_id: filterValue("folder_id") }),
    ...(search.unread === "all" ? {} : { unread: search.unread === "true" }),
    ...(search.due_state.length === 0 ? {} : { due_state: search.due_state }),
    ...(search.received_from === ""
      ? {}
      : { received_from: search.received_from }),
    ...(search.received_to === "" ? {} : { received_to: search.received_to }),
    ...(search.due_from === "" ? {} : { due_from: search.due_from }),
    ...(search.due_to === "" ? {} : { due_to: search.due_to }),
    ...(search.signature === "all"
      ? {}
      : { has_signature: search.signature === "signed" }),
  };
}
